import React, { useState, useCallback, useEffect, useRef } from "react";
import { TextField, TextFieldProps } from "@mui/material";

/**
 * Formats a numeric string with "." as thousand separator.
 * Keeps the decimal part (after ",") untouched.
 * Examples: "1234" → "1.234", "1234567,89" → "1.234.567,89"
 */
function formatWithSeparators(raw: string): string {
    if (!raw) return "";

    const [intPart, ...decParts] = raw.split(",");
    const digits = intPart.replace(/\./g, "");
    if (!digits) return raw;

    const formatted = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return decParts.length > 0 ? `${formatted},${decParts.join("")}` : formatted;
}

/** Strips thousand separators and normalises "," → "." for parseFloat. */
function toNumericString(display: string): string {
    return display.replace(/\./g, "").replace(",", ".");
}

/** Converts a programmatic number/string value into display format. */
function valueToDisplay(value: string | number): string {
    if (value === "" || value == null) return "";
    const n = typeof value === "number" ? value : parseFloat(String(value));
    if (!Number.isFinite(n)) return "";
    const str = String(value);
    const normalized = str.replace(".", ",");
    return formatWithSeparators(normalized);
}

/** Strips redundant leading zeros from the integer digit run (e.g. "09" → "9", "0" stays "0"). */
function normalizeIntegerDigits(digits: string): string {
    if (!digits) return "";
    if (digits === "0") return "0";
    const stripped = digits.replace(/^0+/, "");
    return stripped === "" ? "0" : stripped;
}

export interface MoneyInputProps
    extends Omit<TextFieldProps, "value" | "onChange" | "type"> {
    /** Current raw value (number or string like "1234.56"). */
    value: string | number;
    /** Called with the raw numeric string (e.g. "1234.56") on every change. */
    onValueChange: (raw: string) => void;
    /** Max decimal digits allowed (default 2). */
    decimals?: number;
}

const MoneyInput = React.forwardRef<HTMLDivElement, MoneyInputProps>(
    function MoneyInput({ value, onValueChange, decimals = 2, onFocus, ...textFieldProps }, ref) {
        const [display, setDisplay] = useState(() => valueToDisplay(value));
        const internalChange = useRef(false);

        useEffect(() => {
            if (internalChange.current) {
                internalChange.current = false;
                return;
            }
            setDisplay(valueToDisplay(value));
        }, [value]);

        const handleChange = useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => {
                let raw = e.target.value;

                // Allow only digits, commas (decimal sep) and dots (will be thousand sep)
                raw = raw.replace(/[^0-9.,]/g, "");

                // Normalise: user may type "." intending decimal — keep last comma as decimal
                const commaCount = (raw.match(/,/g) || []).length;
                if (commaCount > 1) {
                    const idx = raw.lastIndexOf(",");
                    raw = raw.slice(0, idx).replace(/,/g, "") + raw.slice(idx);
                }

                // Limit decimal digits
                const commaIdx = raw.indexOf(",");
                if (commaIdx !== -1 && raw.length - commaIdx - 1 > decimals) {
                    raw = raw.slice(0, commaIdx + 1 + decimals);
                }

                const intSection = commaIdx === -1 ? raw : raw.slice(0, commaIdx);
                const decSection = commaIdx === -1 ? "" : raw.slice(commaIdx + 1);
                const intDigits = intSection.replace(/\./g, "");
                const normalizedInt = normalizeIntegerDigits(intDigits);
                const rebuilt =
                    commaIdx === -1 ? normalizedInt : `${normalizedInt},${decSection}`;

                const formatted = formatWithSeparators(rebuilt);
                setDisplay(formatted);

                internalChange.current = true;
                onValueChange(toNumericString(formatted));
            },
            [decimals, onValueChange],
        );

        const handleFocus = useCallback(
            (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                onFocus?.(e);
                const n = typeof value === "number" ? value : parseFloat(String(value));
                if (Number.isFinite(n) && n === 0) {
                    e.target.select();
                }
            },
            [value, onFocus],
        );

        return (
            <TextField
                ref={ref}
                inputMode="decimal"
                {...textFieldProps}
                value={display}
                onChange={handleChange}
                onFocus={handleFocus}
            />
        );
    },
);

export default MoneyInput;
